'use client';

import { faInbox } from '@fortawesome/free-solid-svg-icons';
import { Blockquote, EmptyState, Progress, Spinner } from '@ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBankAccounts } from '~/components/Banking/shared/hooks/useBankAccounts';
import { useToastStore } from '~/components/ToastManager/useToasts';
import { amountInputToCents } from '../amountField';
import { isLedgerDecisionError, useDocumentMandant, useLedgerDecision } from '../hooks/useLedgerDecision';
import type { LedgerDecision } from '../LedgerDecisionDialog';
import BelegDropzone from './BelegDropzone';
import BelegForm from './BelegForm';
import BelegQueue from './BelegQueue';
import BelegRail from './BelegRail';
import DocumentPreview from './DocumentPreview';
import ScannerDialog from './ScannerDialog';
import type { CaptureDocument, ExternalInvoiceData, SelectedTransaction } from './types';
import useApiRequest from './useApiRequest';
import useCaptureSession from './useCaptureSession';

interface Props {
    clubSlug: string;
    /** Files dropped on the list; uploaded as soon as the session exists. */
    initialFiles?: File[];
    /** Opens straight into the pairing view, for entries that are already about the phone. */
    initialScanner?: boolean;
    onSaved?: () => void;
}

const isOpen = (document: CaptureDocument) => 'ready' === document.status || 'failed' === document.status;

/**
 * Works a stack of receipts off one at a time: document on the left, its fields on the right,
 * the payment it settles underneath — and the next one already extracting in the background.
 */
const BelegAssistant = ({ clubSlug, initialFiles, initialScanner, onSaved }: Props) => {
    const { t } = useTranslation('invoice');
    const api = useApiRequest();
    const { addToast } = useToastStore();
    const { data: bankAccounts } = useBankAccounts();
    const ledgerDecision = useLedgerDecision(useDocumentMandant(clubSlug));

    const capture = useCaptureSession({ clubSlug });
    // Destructured so the effects below depend on the stable callbacks rather than on the hook's
    // return object, which is a fresh literal on every render.
    const { session, documents, isStarting, pendingUploads, uploadFiles } = capture;

    const [activeId, setActiveId] = useState<number | null>(null);
    const [selectedTransaction, setSelectedTransaction] = useState<SelectedTransaction | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showScanner, setShowScanner] = useState(Boolean(initialScanner));
    const seededFiles = useRef(false);
    const closeSessionRef = useRef(capture.closeSession);
    const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const apiHost = useMemo(() => process.env.NEXT_PUBLIC_API_HOST ?? '', []);

    useEffect(() => {
        closeSessionRef.current = capture.closeSession;
    }, [capture.closeSession]);

    // Closing the window closes the session, so a phone still sitting on the scanner screen is told
    // rather than left to run into a 410 on its next upload.
    //
    // Deferred by a tick because React 18's dev remount unmounts and mounts again within one —
    // closing right away would tear down the session the hook had just created.
    useEffect(() => {
        if (closeTimer.current) clearTimeout(closeTimer.current);

        return () => {
            closeTimer.current = setTimeout(() => void closeSessionRef.current(), 0);
        };
    }, []);

    // Files the user dropped on the list before the session existed.
    useEffect(() => {
        if (seededFiles.current || !session || !initialFiles?.length) return;
        seededFiles.current = true;
        void uploadFiles(initialFiles);
    }, [initialFiles, session, uploadFiles]);

    const workable = useMemo(() => documents.filter(isOpen), [documents]);
    const active = useMemo(
        () => workable.find((document) => document.id === activeId) ?? workable[0] ?? null,
        [activeId, workable]
    );

    // Switching documents drops the pick with it: a transaction chosen for one receipt would
    // otherwise be booked against the next.
    const selectDocument = useCallback((documentId: number | null) => {
        setActiveId(documentId);
        setSelectedTransaction(null);
    }, []);

    const confirmedCount = documents.filter((document) => 'confirmed' === document.status).length;
    const totalCount = documents.filter((document) => 'discarded' !== document.status).length;

    const advance = useCallback(
        (bookedId: number) => {
            capture.markConfirmed(bookedId);
            const next = workable.find((document) => document.id !== bookedId && 'ready' === document.status);
            selectDocument(next?.id ?? null);
        },
        [capture, selectDocument, workable]
    );

    const handleSubmit = useCallback(
        async (values: FieldValues) => {
            if (!active) return;

            const decision = await ledgerDecision.resolve(values?.document_date ?? values?.due_date);
            if (decision === null) return;

            setIsSubmitting(true);

            // Both are form-only: the account picker writes its own field name, and the type is a
            // select where the column is a boolean.
            const { user_invoice_account_number: accountNumber, document_type: documentType, ...rest } = values ?? {};
            // Dropped from the form, but RHF still submits defaultValues for fields it no longer
            // renders — so the extracted country would keep landing in a column nothing shows.
            delete rest.country;
            const cents = amountInputToCents(values?.value);

            const payload = {
                ...rest,
                account_number: accountNumber ?? values?.account_number ?? null,
                is_credit: 'credit' === documentType ? 1 : 0,
                tax_code: values?.tax_code || null,
                value: cents,
                file_id: active.file_id ?? null,
                capture_document_id: active.id
            };

            // The shared Form feeds whatever this resolves with to setValidationErrors, so a
            // failed store has to come back as the axios error for a 422 to reach the fields.
            let requestError: unknown;

            const store = (ledger_decision?: LedgerDecision) =>
                api({
                    url: `invoice-external/${clubSlug}/store`,
                    method: 'POST',
                    data: ledger_decision ? { ...payload, ledger_decision } : payload,
                    muteWhen: (response) => !ledger_decision && isLedgerDecisionError(response),
                    onError: (error: unknown) => {
                        requestError = error;
                    }
                });

            let stored = await store(decision);

            if (!stored && !decision) {
                const retried = await ledgerDecision.retry(requestError);

                if (retried) {
                    requestError = undefined;
                    stored = await store(retried);
                }
            }

            const invoiceId = stored?.data?.id;

            if (!invoiceId) {
                setIsSubmitting(false);

                return requestError;
            }

            // The invoice exists and is already in the ledger from here on, so a failing link is
            // a warning — never a reason to unwind the booking.
            if (selectedTransaction) {
                await api({
                    url: `invoice-external/${clubSlug}/${invoiceId}/transaction`,
                    method: 'PUT',
                    data: selectedTransaction,
                    muted: true,
                    onFailure: () =>
                        addToast({
                            type: 'alert',
                            title: t('belegAssistant.toasts.linkFailedTitle'),
                            description: t('belegAssistant.toasts.linkFailedDescription')
                        })
                });
            }

            setIsSubmitting(false);
            advance(active.id);
            onSaved?.();
        },
        [active, addToast, advance, api, clubSlug, ledgerDecision, onSaved, selectedTransaction, t]
    );

    if (isStarting && documents.length === 0) {
        return (
            <div className="tw-flex tw-h-64 tw-items-center tw-justify-center">
                <Spinner />
            </div>
        );
    }

    const extraction: ExternalInvoiceData = active?.extraction ?? {};
    const remaining = workable.filter((document) => document.id !== active?.id).length;

    return (
        <div className="tw-flex tw-h-[80vh] tw-min-h-0 tw-flex-col tw-gap-4">
            <div className="tw-flex tw-shrink-0 tw-flex-col tw-gap-1">
                <span className="tw-text-sm tw-font-semibold">
                    {totalCount > 0
                        ? t('belegAssistant.header.progress', { done: confirmedCount, total: totalCount })
                        : t('belegAssistant.header.empty')}
                </span>
                <Progress color="success" size="xs" progress={totalCount > 0 ? confirmedCount / totalCount : 0} />
            </div>

            <div className="tw-grid tw-min-h-0 tw-flex-1 tw-gap-4 lg:tw-grid-cols-[260px_minmax(0,1fr)_minmax(0,1fr)]">
                {/* The rail: what is in the stack, how to add to it, and — while pairing — the
                    code that lets a phone add to it too. */}
                <div className="tw-flex tw-h-full tw-min-h-0 tw-flex-col tw-gap-3">
                    <BelegQueue
                        documents={documents}
                        pendingUploads={pendingUploads}
                        activeId={active?.id ?? null}
                        onSelect={selectDocument}
                        onDiscard={(id) => void capture.discardDocument(id)}
                        onRetry={(id) => void capture.retryDocument(id)}
                    />
                    {showScanner && session ? (
                        <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-pr-1">
                            <ScannerDialog
                                session={session}
                                onRequestScan={capture.requestScan}
                                onClose={() => setShowScanner(false)}
                            />
                        </div>
                    ) : (
                        <BelegDropzone
                            fill
                            onFiles={(files) => void uploadFiles(files)}
                            onScanRequest={() => setShowScanner(true)}
                        />
                    )}
                </div>

                {active ? (
                    <>
                        <DocumentPreview
                            className="tw-border-0 tw-border-l tw-border-r tw-border-solid tw-border-accents-2 tw-px-4"
                            key={`preview-${active.id}`}
                            url={active.file_id ? `${apiHost}invoice-external/${clubSlug}/capture/documents/${active.id}/file` : null}
                        />

                        <div className="tw-flex tw-min-h-0 tw-flex-col tw-gap-3">
                            {'failed' === active.status && (
                                <Blockquote color="error" size="sm">
                                    {t(`belegAssistant.errors.${active.error_code ?? 'ocr_failed'}`)}
                                </Blockquote>
                            )}

                            <BelegForm
                                key={`form-${active.id}`}
                                data={extraction}
                                isSubmitting={isSubmitting}
                                submitLabel={
                                    remaining > 0
                                        ? t('belegAssistant.actions.saveAndNext', { count: remaining })
                                        : t('belegAssistant.actions.save')
                                }
                                onSubmit={handleSubmit}
                                rail={
                                    <BelegRail
                                        clubSlug={clubSlug}
                                        hasBankAccounts={bankAccounts.length > 0}
                                        selected={selectedTransaction}
                                        onSelect={setSelectedTransaction}
                                    />
                                }
                            />
                        </div>
                    </>
                ) : (
                    <div className="tw-col-span-2 tw-flex tw-items-center tw-justify-center">
                        <EmptyState
                            icon={faInbox}
                            title={t('belegAssistant.empty.title')}
                            description={t('belegAssistant.empty.description')}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default BelegAssistant;
